import axios from "axios";
import { OpenAPI } from "@propertelligent/client-api";

export interface DeletePaymentDueAccountGroupResult {
  candidateCount: number;
  deletedCount: number;
  skippedCount: number;
  message: string;
}

export interface PayPaymentDueAccountGroupResult {
  candidateCount: number;
  paidCount: number;
  skippedCount: number;
  /**
   * Plaid/Default items reserved for transfer (AutoPayTransferPending) and awaiting
   * TransferMoneyEventConsumer confirmation -- not yet reflected in paidCount.
   * Hand-written shim field: not part of the generated @propertelligent/client-api client
   * for this custom endpoint's result shape, so it won't appear from a client-api bump.
   * Optional: no runtime validation on the raw axios response, so an older/rolled-back
   * API can omit it -- callers must treat a missing value as 0, not as truthy pending work.
   */
  pendingCount?: number;
  message: string;
}

/** Per-id amount override for PUT /pay/group — omit entry = full outstanding for that id. */
export interface BulkPayAmount {
  paymentDueAccountId: number;
  amount: number;
}

export interface BulkPayRequest {
  paymentDueAccountIds: number[];
  /** Partial (or explicit) amounts; empty/omit = full payoff for listed ids. */
  amounts?: BulkPayAmount[];
  fromPaymentMethodId?: number;
  paymentMethodTypeId: number; // PaymentMethodTypesEnum
  checkNumber?: string;
  paymentDate: string; // ISO 8601
  transactionId?: string;
  assistanceProvider?: string;
  /** Client-generated UUID (one per click); server derives per-item keys from it. */
  idempotencyKey?: string;
}

export type PartialPayModeState = {
  mode: "full" | "partial" | null;
  partialAmount?: number;
};

export interface PaymentDeleteOutcome {
  shouldRefresh: boolean;
  notificationType: "success" | "warning";
}

export interface SingleItemResponse<T> {
  success?: T;
  errors?: Record<string, string[]>;
}

export interface LineOutstanding {
  paymentDueAccountId: number;
  outstandingAmount: number;
}

export interface GroupedPaymentDueAccountLike {
  id?: number;
  paymentDueAccountIds?: number[];
  /** Per-line outstanding from grouped listing; optional until client-api publishes. */
  lineOutstandings?: LineOutstanding[];
}

export interface ApiHelperResponse<T> {
  data?: T | null;
  errors?: unknown;
  statusCode?: number | null;
}

export type DeletePaymentActionStatus = "success" | "failure";

export interface DeletePaymentAction {
  status: DeletePaymentActionStatus;
  shouldRefresh: boolean;
  notificationType: "success" | "warning" | "failure";
  message?: string;
}

const baseUrl = (): string =>
  typeof OpenAPI.BASE === "string" ? OpenAPI.BASE : "";

const authHeaders = (): Record<string, string> => {
  const token =
    typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : undefined;
  return token ? { Authorization: ["Bearer", token].join(" ") } : {};
};

export const deletePaymentDueAccountGroup = async (
  paymentDueAccountIds: number[],
): Promise<SingleItemResponse<DeletePaymentDueAccountGroupResult>> => {
  const response = await axios.delete(
    `${baseUrl()}/api/PaymentDueAccount/group`,
    {
      data: { paymentDueAccountIds },
      headers: authHeaders(),
    },
  );
  return response.data;
};

export const getPaymentDeleteOutcome = (
  result: DeletePaymentDueAccountGroupResult,
): PaymentDeleteOutcome => ({
  shouldRefresh: result.deletedCount > 0,
  notificationType:
    result.deletedCount > 0 && result.skippedCount === 0
      ? "success"
      : "warning",
});

export const payPaymentDueAccountGroup = async (
  request: BulkPayRequest,
): Promise<SingleItemResponse<PayPaymentDueAccountGroupResult>> => {
  const response = await axios.put(
    `${baseUrl()}/api/PaymentDueAccount/pay/group`,
    request,
    { headers: authHeaders() },
  );
  return response.data;
};

export const getPaymentPayOutcome = (
  result: PayPaymentDueAccountGroupResult,
): PaymentDeleteOutcome => ({
  // Reuses the existing {shouldRefresh, notificationType} shape -- same semantics as
  // getPaymentDeleteOutcome, with paidCount/pendingCount in place of deletedCount.
  // A Plaid payment that was only reserved (pendingCount > 0) still needs a refresh so
  // the settle list picks up its new pending state, but must not read as a clean "success"
  // until it's actually confirmed paid -- see bug/plaid-optimistic-paid.
  // pendingCount is optional (hand-written shim field, no runtime validation) -- an
  // absent value must behave the same as 0, never as "there's pending work".
  shouldRefresh: result.paidCount > 0 || (result.pendingCount ?? 0) > 0,
  notificationType:
    result.paidCount > 0 &&
    result.skippedCount === 0 &&
    !result.pendingCount
      ? "success"
      : "warning",
});

export const resolveGroupedPaymentIds = (
  payment: GroupedPaymentDueAccountLike,
): number[] =>
  (payment.paymentDueAccountIds?.length
    ? payment.paymentDueAccountIds
    : [payment.id]
  ).filter((paymentId): paymentId is number => Number(paymentId) > 0);

/**
 * FIFO-allocates a single partial total across grouped line outstandings.
 * Skips empty slices; never emits amount ≤ 0; never exceeds a line's outstanding.
 */
export const allocatePartialAcrossLines = (
  lines: LineOutstanding[],
  partialAmount: number,
): BulkPayAmount[] => {
  const amounts: BulkPayAmount[] = [];
  let remaining = Number(partialAmount);
  if (!(remaining > 0)) {
    return amounts;
  }

  for (const line of lines) {
    if (!(remaining > 0)) {
      break;
    }
    const paymentDueAccountId = Number(line.paymentDueAccountId);
    const outstanding = Number(line.outstandingAmount);
    if (!(paymentDueAccountId > 0) || !(outstanding > 0)) {
      continue;
    }
    const slice = Math.min(remaining, outstanding);
    if (!(slice > 0)) {
      continue;
    }
    amounts.push({ paymentDueAccountId, amount: slice });
    remaining -= slice;
  }

  return amounts;
};

/**
 * Builds `amounts` overrides for settle bulk-pay in Partial mode.
 * Single-id rows keep a one-entry override; multi-id rows waterfall across
 * `lineOutstandings` (or a one-line fallback from the first id + amount).
 */
export const buildPartialBulkPayAmounts = (
  selectedPayments: Array<GroupedPaymentDueAccountLike & { amount?: number }>,
  paymentStates: Record<string, PartialPayModeState>,
): BulkPayAmount[] => {
  const amounts: BulkPayAmount[] = [];
  for (const payment of selectedPayments) {
    const ids = resolveGroupedPaymentIds(payment);
    if (ids.length === 0) continue;
    const state = paymentStates[String(payment.id)];
    if (state?.mode !== "partial") continue;
    const amount = Number(
      payment.amount ?? state.partialAmount ?? 0,
    );
    if (!(amount > 0)) continue;
    if (ids.length === 1) {
      amounts.push({ paymentDueAccountId: ids[0], amount });
      continue;
    }
    const lines =
      payment.lineOutstandings && payment.lineOutstandings.length > 0
        ? payment.lineOutstandings
        : [{ paymentDueAccountId: ids[0], outstandingAmount: amount }];
    amounts.push(...allocatePartialAcrossLines(lines, amount));
  }
  return amounts;
};

/**
 * Ids to send on PUT /pay/group: Full/null rows expand every grouped id;
 * Partial rows include only ids that received a waterfall amount (fallback: all ids).
 */
export const collectSettlePayGroupIds = (
  selectedPayments: Array<GroupedPaymentDueAccountLike>,
  paymentStates: Record<string, PartialPayModeState>,
  amounts?: BulkPayAmount[] | null,
): number[] => {
  const allocatedIdSet = new Set(
    (amounts ?? [])
      .filter(
        (entry) =>
          Number(entry.paymentDueAccountId) > 0 && Number(entry.amount) > 0,
      )
      .map((entry) => entry.paymentDueAccountId),
  );

  const collected: number[] = [];
  for (const payment of selectedPayments) {
    const ids = resolveGroupedPaymentIds(payment);
    const mode = paymentStates[String(payment.id)]?.mode;
    const allocatedIds = ids.filter((id) => allocatedIdSet.has(id));
    const isPartial =
      mode === "partial" || (mode == null && allocatedIds.length > 0);

    if (isPartial) {
      collected.push(...(allocatedIds.length > 0 ? allocatedIds : ids));
    } else {
      collected.push(...ids);
    }
  }
  return collected;
};

export const mapDeleteResponseToAction = (
  apiHelperResponse:
    | ApiHelperResponse<SingleItemResponse<DeletePaymentDueAccountGroupResult>>
    | null
    | undefined,
): DeletePaymentAction => {
  if (
    !apiHelperResponse ||
    apiHelperResponse.statusCode ||
    apiHelperResponse.errors
  ) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const result = apiHelperResponse.data?.success;
  if (!result) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const outcome = getPaymentDeleteOutcome(result);
  return {
    status: "success",
    shouldRefresh: outcome.shouldRefresh,
    notificationType: outcome.notificationType,
    message: result.message,
  };
};

export const mapPayResponseToAction = (
  apiHelperResponse:
    | ApiHelperResponse<SingleItemResponse<PayPaymentDueAccountGroupResult>>
    | null
    | undefined,
): DeletePaymentAction => {
  if (
    !apiHelperResponse ||
    apiHelperResponse.statusCode ||
    apiHelperResponse.errors
  ) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const result = apiHelperResponse.data?.success;
  if (!result) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const outcome = getPaymentPayOutcome(result);
  return {
    status: "success",
    shouldRefresh: outcome.shouldRefresh,
    notificationType: outcome.notificationType,
    message: result.message,
  };
};
